import type { PlatformRouteView } from "@tdelivr/api-client/generated/experience/api-gateway.js";

import type { BoardRow } from "./board.ts";

/**
 * What the gateway routes board does to the rows EXP-001 returns, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `gateway`, against EXP-001
 * `GET /v1/platform/gateway/routes` — published by
 * [ADR 0051](../../../docs/adr/0051-a-platform-scoped-gateway-route-listing-is-a-separate-operation-and-a-separate-scope.md)
 * because the only listing a platform route board could reach said it was "filtered to the tenant on
 * the caller's verified token", and a platform route table spans the platform (ADR 0048).
 *
 * ```
 * gateway:['Gateway routes','API-01','Publishing a route changes authorisation for the whole
 *   platform. Every version needs a requester and a different approver.',
 *   [['Routes','142','done','published'],['Awaiting approval','2','human','needs approver'],
 *    ['Rate-limited now','3','caution','bursting'],['Disabled','1','closed','by approval']],
 *   ['Route','Environment','Scopes'],
 *   [['/v1/ride-offers','production','ride-offer:read + workload','done','published v7'],
 *    ['/v1/payment-executions','production','payment:execute','human','v12 awaiting approval'],
 *    ['/v1/delivery-quotes','staging','delivery-quote:create','done','published v3'],
 *    ['/v1/stop-plans','production','stop-plan:write','caution','rate limited']],
 *   'lock','Uses the approval pattern from deliverable 11: requestedBy and approvedBy must differ.']
 * ```
 *
 * ## Three columns with a source, one badge grammar of four, and a purpose line with none
 *
 * **A platform-scoped read was not enough to finish this page.** `Route`, `Environment` and
 * `Scopes` are published and so is `published v7`. What is not published **anywhere a browser can
 * call** is who requested a route version and who approved it — which is the page's own purpose
 * sentence, "Every version needs a requester and a different approver".
 *
 * API-01 holds both and publishes them under `/internal/v1`:
 * `GET /internal/v1/tenants/{tenantId}/gateway/routes/{routeKey}/{environment}` answers
 * `requestedBy`, `approvedBy` and `approvalReference`, and the gateway does not expose that surface
 * (ADR 0029 §2). **This is narrower than the claim this repository had been carrying** — "swept for
 * `requestedBy`, `approvedBy`, `requester` and `approver` and none exists" — which a sweep with a
 * positive control contradicts: thirteen operations publish `requestedBy`, twelve publish
 * `approvedBy`, and FIN-07 publishes `approvedBy` on `/v1`. Only the two names `requester` and
 * `approver` are used by nothing. Q-188 carries the correction, and it matters because it changes
 * the fix from modelling work to a public-surface decision API-01 takes.
 *
 * So this board draws the three columns it can and **does not add a column nothing maintains**.
 * The requester and the approver appear nowhere on it, not even as an empty column, because the
 * design draws no column for them either: they belong to its purpose line, its note, its
 * `Awaiting approval · 2 · needs approver` tile and its `History` block. Each of those is recorded
 * rather than invented, and **Q-188** carries all of them together.
 *
 * ## Only loaded routes, so three of the design's four badges cannot occur
 *
 * This read answers the table **one gateway process is serving**, and a route reaches that table
 * only when it is published, enabled, for this environment, public, scope-bearing and
 * unambiguous — `loadRouteTable` refuses the rest. So:
 *
 *   - `done · published v7` is reachable and is every row's badge.
 *   - `human · v12 awaiting approval` cannot occur. A version awaiting approval is not published,
 *     so no gateway has loaded it. Q-188.
 *   - `caution · rate limited` cannot occur. It is a live observation of traffic against a limit,
 *     and ADR 0051 withholds the limits themselves across the tenancy boundary — one tenant's
 *     negotiated capacity is not another operator's business. Q-191.
 *   - `closed · by approval`, the `Disabled · 1` tile's state, cannot occur. A disabled route is
 *     absent from the table rather than present in it. Q-188.
 *
 * A board that can draw one badge is a board whose badge column informs nobody, which is the finding
 * `apps/tenant-admin-console/src/catalogue.ts` records about MOB-01's constant `lifecycle` and
 * ADR 0045 §2 about CFG-02's constant `state`. **It is drawn anyway**, because `CLAUDE.md` section 1
 * says not to remove what the design draws, and the reason is on the badge's `title` rather than
 * inferred. Q-188 records the finding; this file does not act on it.
 *
 * ## `ride-offer:read + workload` is a value this read cannot produce, and the design is wrong
 *
 * That cell pairs a scope with the word `workload`, which is `authenticationMode`. A route whose
 * authentication mode is `workload` is **refused at load** — ADR 0029 §2, and `route-table.ts`
 * refuses it with `workload_route_not_public` because such a route describes a caller acting for a
 * whole tenant, which the public surface has no audience for. So no loaded route can ever be a
 * workload route, and no row of this board can ever read `+ workload`. The cell is the scopes,
 * joined by the design's own ` + `. Q-188.
 */

/** The design's own scopes grammar: `ride-offer:read + workload`, two scopes joined by ` + `. */
export function scopesText(route: PlatformRouteView): string {
  return route.requiredScopes.join(" + ");
}

/**
 * The badge, which is the one state a loaded route can be in.
 *
 * `done · published v7`, from the design's own first row, with the reason the other three badges
 * cannot occur on the `title` — because an operator looking at a column of identical badges should
 * be able to find out whether that is the platform's state or the read's limit.
 */
export function stateOf(route: PlatformRouteView): {
  classKey: "done";
  label: string;
  title: string;
} {
  return {
    classKey: "done",
    label: `published v${String(route.version)}`,
    title:
      "Every row of this board is a published, enabled route: EXP-001 answers the table it is serving, and it refuses to load anything else. A version awaiting approval, a disabled route and a route being rate-limited now are all states this read cannot answer. Q-188.",
  };
}

/**
 * One row of the gateway routes board.
 *
 * `environment` is the gateway's, not the route's: the read answers one value for the whole table,
 * because a gateway process serves one of API-01's four environments and refuses every route
 * published for another. So the `Environment` column is the same word on every row — true, and the
 * design's own two values (`production`, `staging`) cannot both appear in one answer. Q-191.
 */
export function routeRow(route: PlatformRouteView, environment: string): BoardRow {
  return {
    /*
     * The tenant and the route key, which is what API-01's policy is unique by within an
     * environment. Prefixed with the tenant because this listing spans tenants and two tenants may
     * publish the same prefix — a React key that collided would make one tenant's route replace
     * another's on a board whose purpose is seeing every tenant's.
     */
    id: `${route.tenantId}:${route.routeKey}`,
    cells: [
      // `/v1/…` matches the design's own mono prefix list; `production` and the scope list do not,
      // so those two are Geist. The design draws `ride-offer:read + workload` in Geist at 400 even
      // though it contains a colon — its *form* chips use the colon as a mono trigger and its grid
      // cells use the prefix list, and this is a grid cell.
      { text: route.pathPrefix, mono: true },
      { text: environment },
      { text: scopesText(route) },
    ],
    state: stateOf(route),
    detail: [
      { label: "Tenant", value: route.tenantId, mono: true },
      { label: "Route key", value: route.routeKey, mono: true },
      { label: "Version", value: `v${String(route.version)}` },
      {
        label: "Required scopes",
        value: route.requiredScopes.join(", "),
        mono: true,
        ...(route.requiredScopes.length === 0 ? { absent: true } : {}),
      },
    ],
    /*
     * The design's `History` block, and this is the page where it is emptiest.
     *
     * It reads `Created`, `Last changed` and `Version` with a person's name beside the first two.
     * The version is published; the two instants and both names are API-01's governed history, which
     * is `/internal/v1` only. So one entry rather than three, and Q-107 and Q-188 are where the rest
     * lives. The alternative — three labels with `—` beside two of them — would be a panel whose
     * shape promised something, and the one entry is the honest shape.
     */
    history: [{ label: "Version", value: `v${String(route.version)}` }],
    // The design takes the detail panel's glyph from the page's own form icon: `alt_route`.
    glyph: "alt_route",
  };
}

/**
 * The states this listing can answer, which is one.
 *
 * Kept as a list rather than collapsed, because the shape is what the board's filter reads and
 * because an entry appearing here is how a reader would notice this read starting to answer more.
 */
export function statesPresent(routes: readonly PlatformRouteView[]): readonly string[] {
  return [...new Set(routes.map((route) => stateOf(route).label))].sort();
}

/**
 * What the board owes an operator about the rows it is not showing.
 *
 * Two facts, in the order that changes what they should conclude:
 *
 *  1. **It is one gateway's loaded table, not API-01's history.** A route that was approved and is
 *     not being served is absent from this board, and that is precisely the case an operator opens
 *     it to investigate. The tenant-bounded read beside it publishes the refusal list for the
 *     caller's own tenant; ADR 0051 withholds it across tenants, so this board cannot say which
 *     routes were refused or why. Q-192.
 *  2. **How many tenants the rows came from**, said rather than counted against a roster, because
 *     nothing publishes how many tenants exist — the same sentence `platform-audit.ts` writes two
 *     pages over.
 */
export function tableBoundNote(
  routes: readonly PlatformRouteView[] | undefined,
  environment: string | undefined,
): string | undefined {
  if (routes === undefined || environment === undefined) return undefined;
  const tenants = new Set(routes.map((route) => route.tenantId)).size;
  return `Routes from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}, loaded for ${environment}. This is the table one gateway is serving: a route that was approved and is not being served is absent rather than shown with a reason, because the refusal list is not published across tenants. Q-192.`;
}

/**
 * Why the designed `New gateway route version` form is drawn and refused.
 *
 * Its sections are `Route` — Path, Environment, Upstream service — and `Access` — Required scopes —
 * and its primary is on API-01. Three reasons, and any one is enough:
 *
 *   - **The publish command is `/internal/v1` only**:
 *     `POST /internal/v1/tenants/{tenantId}/gateway/routes/{routeKey}/versions`, which the gateway
 *     does not expose.
 *   - **It is API-01's command and this board reads EXP-001.** Publishing a route version is a
 *     control-plane write; the data plane applies what loaded. ADR 0029 is the division.
 *   - **A version needs a requester and a different approver**, which is the page's own purpose
 *     line, and the form has a field for neither — so one administrator pressing it could not
 *     satisfy the approval pattern even if the route were public. Q-188.
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` section 1 says the add or edit form is
 * not to be removed, and rather than wired to something that would answer 404.
 */
export const PUBLISH_REFUSAL =
  "API-01 publishes the route version write on /internal/v1 only, which the gateway does not expose, and a version needs a requester and a different approver — nothing publishes either and this form has a field for neither. Q-188, ADR 0051.";
